import { Util, devLog } from "utils";
import { TotalMapType } from "apis/library/library.api";
import { HOST } from "consts/env";
import { FetchManager } from "services/FetchManager";
import {
  GroupInfoType,
  GroupListType,
  GroupNameType,
  UserListType,
} from "types/admin/AdminType";
import { create } from "zustand";

export type UserListReqType = {
  groupId: string;
  page: number;
  rows: number;
};

export interface GroupStoreType {
  groupList: GroupListType[];
  groupTotalMap: TotalMapType;
  currentGroupDetail: GroupInfoType /** 그룹 수정 모달 출력 시 해당하는 그룹 정보  */;

  getGroupDetail: (groupId: string) => Promise<GroupInfoType>;
  getGroupList: () => Promise<boolean>;
  getGroupNameList: (
    isMyGroup: boolean
  ) => Promise<any> /** @param isMyGroup : User가 본인이 속한 그룹만 list에 보여지도록 하는 유무 @author c_bae */;

  getGroupDetailMemberList: (form: UserListReqType) => Promise<UserListType[]>;
  getGroupDetailNonMemberList: (
    form: UserListReqType
  ) => Promise<UserListType[]>;

  createGroup: (form: GroupInfoType) => Promise<string>;
  patchGroupDetail: (form: GroupInfoType) => Promise<string>;
  deleteGroup: (groupId: string) => Promise<boolean>;

  getMyGroupList: (userId: string) => Promise<GroupNameType[]>;
  resetGroupList: () => void;
}

const useGroupAPIStore = create<GroupStoreType>((set) => ({
  groupList: [],
  groupTotalMap: { totalRows: 0, totalPages: 0, allRows: 0 },
  currentGroupDetail: {
    groupId: "",
    groupName: "",
    groupDescription: "",
    groupMembers: [],
  },

  async getGroupDetail(groupId: string) {
    try {
      const url = `${HOST}/groups/${groupId}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data?.resultObject) {
        set({ currentGroupDetail: data.resultObject });
        return data.resultObject;
      } else throw new Error();
    } catch (e) {
      devLog("[getGroupDetail] 오류 발생", e);
      return false;
    }
  },

  async getGroupList() {
    try {
      const url = `${HOST}/groups`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        set({ groupList: data.resultList });
        set({ groupTotalMap: data.totalMap });
        return true;
      } else throw new Error();
    } catch (e) {
      devLog("[getGroupList] 오류 발생", e);
      return false;
    }
  },

  async getGroupNameList(isMyGroup: boolean) {
    try {
      const url = `${HOST}/groups/names?filterFlag=${isMyGroup}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        const {
          resultList,
          code,
        }: { resultList: GroupNameType[]; code: number } = data;
        return { resultList, code };
      } else throw new Error();
    } catch (e) {
      devLog("[getGroupNameList] 오류 발생", e);
      return false;
    }
  },

  async getGroupDetailMemberList(form: UserListReqType) {
    try {
      const url = `${HOST}/groups/members?page=${form.page}&rows=${form.rows}&groupId=${form.groupId}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        return data.resultList;
      } else throw new Error();
    } catch (e) {
      devLog("[getGroupDetailMemberList] 오류 발생", e);
      return false;
    }
  },

  async getGroupDetailNonMemberList(form: UserListReqType) {
    try {
      const url = `${HOST}/groups/non-members?page=${form.page}&rows=${form.rows}&groupId=${form.groupId}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        return data.resultList;
      } else throw new Error();
    } catch (e) {
      devLog("[getGroupDetailNonMemberList] 오류 발생", e);
      return false;
    }
  },

  async createGroup(form: GroupInfoType) {
    try {
      const url = `${HOST}/groups`;
      const res = await FetchManager.post(url, {
        ...form,
        groupName: Util.normalizeSearch(form.groupName),
        groupDescription: Util.normalizeSearch(form.groupDescription),
      });
      const data = await Util.fetchAndSetData(res);

      if (data?.resultObject) {
        return data.resultObject;
      } else throw new Error();
    } catch (e) {
      devLog("[createGroup] 오류 발생", e);
      return false;
    }
  },

  async patchGroupDetail(form: GroupInfoType) {
    try {
      const url = `${HOST}/groups/${form.groupId}`;
      const res = await FetchManager.patch(url, {
        ...form,
        groupName: Util.normalizeSearch(form.groupName),
        groupDescription: Util.normalizeSearch(form.groupDescription),
      });
      const data = await Util.fetchAndSetData(res);

      if (data?.resultObject) {
        return data.resultObject;
      } else throw new Error();
    } catch (e) {
      devLog("[patchGroupDetail] 오류 발생", e);
      return false;
    }
  },

  async deleteGroup(groupId: string) {
    try {
      const url = `${HOST}/groups/${groupId}`;
      const res = await FetchManager.delete(url);
      const data = await Util.fetchAndSetData(res);

      if (data?.isSuccess) {
        return true;
      } else throw new Error();
    } catch (e) {
      devLog("[deleteGroup] 오류 발생", e);
      return false;
    }
  },

  async getMyGroupList(userId: string) {
    try {
      const url = `${HOST}/groups/my-groups/${userId}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data) {
        return data.resultList;
      } else throw new Error();
    } catch (e) {
      devLog("[getMyGroupList] 오류 발생", e);
      return false;
    }
  },
  resetGroupList: () => {
    set({
      groupList: [],
      groupTotalMap: { totalRows: 0, totalPages: 0, allRows: 0 },
      currentGroupDetail: {
        groupId: "",
        groupName: "",
        groupDescription: "",
        groupMembers: [],
      },
    });
  },
}));

export default useGroupAPIStore;
